// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TurnerProductInfo } from "@turnerBlueprint/App/Services/TurnerApiModels";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & HasProductContext;

export interface ProductDetailsPartNumberStyles {
    labelText?: TypographyProps;
    valueText?: TypographyProps;
    // XNG-Change
    wrapper?: InjectableCss;
}

export const partNumberStyles: ProductDetailsPartNumberStyles = {
    labelText: {
        size: 15,
        weight: 600,
        css: css`
            margin-right: 10px;
        `,
        transform: "uppercase",
    },
    valueText: {
        size: 15,
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = partNumberStyles;

// CDT custom - supports variant data updating and displaying properly
const ProductDetailsPartNumber: React.FC<Props> = ({ productContext }) => {
    if (!productContext.product) {
        return null;
    }
    const { productInfo, product } = productContext;

    // XNG-Change: add wrappers to match design
    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                {!product!.isVariantParent && (productInfo as TurnerProductInfo)!.brand !== undefined && (
                    <Typography {...styles.valueText} as="p" data-test-selector="ProductDetailsPartNumber">
                        <Typography {...styles.labelText}>{translate("Brand")}</Typography>
                        {(productInfo as TurnerProductInfo)!.productLine!.name}
                    </Typography>
                )}
                {product &&
                    (product!.isVariantParent ||
                        product!.variantTraits === undefined ||
                        product!.variantTraits!.length < 1) &&
                    product!.productLine && (
                        <Typography {...styles.valueText} as="p" data-test-selector="ProductDetailsPartNumber">
                            <Typography {...styles.labelText}>{translate("Brand")}</Typography>
                            {product.productLine.name}
                        </Typography>
                    )}
                <Typography {...styles.valueText} as="p" data-test-selector="ProductDetailsPartNumber">
                    <Typography {...styles.labelText}>{translate("Part #")}</Typography>
                    {productContext.product.productNumber}
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                {/** XNG-Changes: add MFG to achive the order in PDP */}
                {!!productContext.product.manufacturerItem && (
                    <Typography {...styles.valueText} as="p">
                        <Typography {...styles.labelText}>{translate("MFG #")}</Typography>
                        {productContext.product.manufacturerItem}
                    </Typography>
                )}
                {productContext.product!.detail!.upcCode && (
                    <Typography {...styles.valueText} as="p" data-test-selector="ProductDetailsPartNumber">
                        <Typography {...styles.labelText}>{translate("UPC #")}</Typography>
                        {productContext.product!.detail!.upcCode}
                    </Typography>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(ProductDetailsPartNumber),
    definition: {
        displayName: "Part Number",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
